// benchmark-live-theme-cms-v2
const API_BASE = (
  process.env.GATSBY_API_BASE ||
  "https://api.benchmark.ae/v1"
).replace(/\/$/, "")

const WORKSPACE_KEY = (
  process.env.GATSBY_WORKSPACE_KEY ||
  "benchmark-digital-solutions"
).trim()

const ENABLED = /^(1|true|yes)$/i.test(
  String(process.env.GATSBY_ENABLE_WEBSITE_CMS || "false"),
)

const CACHE_KEY = `benchmark-live-theme:${WORKSPACE_KEY}`
const CACHE_MAX_AGE = 7 * 24 * 60 * 60 * 1000

export const cmsApiHost = API_BASE.replace(/\/v1$/, "")
export const websiteCmsEnabled = () => ENABLED

export function readCachedWebsiteContent() {
  if (!ENABLED || typeof window === "undefined") return null
  try {
    const raw = window.localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed?.savedAt || Date.now() - parsed.savedAt > CACHE_MAX_AGE) return null
    return parsed.payload || null
  } catch (_) {
    return null
  }
}

export async function refreshWebsiteContent({ timeoutMs = 4500 } = {}) {
  if (!ENABLED || typeof fetch === "undefined") return null
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null

  try {
    const response = await fetch(`${API_BASE}/public/website/content`, {
      headers: {
        "X-Workspace-Key": WORKSPACE_KEY,
        Accept: "application/json",
      },
      signal: controller ? controller.signal : undefined,
      cache: "default",
    })
    if (!response.ok) throw new Error(`CMS HTTP ${response.status}`)
    const payload = await response.json()

    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem(
          CACHE_KEY,
          JSON.stringify({ savedAt: Date.now(), payload }),
        )
      } catch (_) {}
    }
    return payload
  } catch (error) {
    console.warn("CMS unavailable; current website content remains active.", error)
    return null
  } finally {
    if (timer) clearTimeout(timer)
  }
}

export function findWebsiteBlock(payload, key) {
  if (!payload || !Array.isArray(payload.blocks)) return null
  return payload.blocks.find(block => block?.key === key) || null
}

export function localizedBlockData(payload, key, lang = "en") {
  const data = findWebsiteBlock(payload, key)?.data
  if (!data || typeof data !== "object") return null
  const locale = lang === "ar" ? "ar" : "en"
  return data[locale] && typeof data[locale] === "object" ? data[locale] : null
}

export function blockContent(payload, key) {
  const data = findWebsiteBlock(payload, key)?.data
  return data && typeof data === "object" ? data.content ?? null : null
}

export function absoluteCmsAsset(value) {
  if (!value) return ""
  if (/^https?:\/\//i.test(value)) return value
  return `${cmsApiHost}${value.startsWith("/") ? "" : "/"}${value}`
}

export function productText(product, lang, field) {
  const locale = lang === "ar" ? "ar" : "en"
  return (
    product?.[`${field}_${locale}`] ||
    product?.[`${field}_${locale === "ar" ? "en" : "ar"}`] ||
    ""
  )
}

export function legacyProductPath(product, lang = "en") {
  const locale = lang === "ar" ? "ar" : "en"
  const paths = product?.metadata?.legacy_paths || {}
  return String(
    paths[locale] ||
    paths[locale === "ar" ? "en" : "ar"] ||
    "",
  ).replace(/^\/+/, "")
}

export function productImage(product, lang = "en") {
  const attached =
    product?.banner_image_url ||
    (Array.isArray(product?.image_urls) ? product.image_urls[0] : "")
  if (attached) return absoluteCmsAsset(attached)

  const locale = lang === "ar" ? "ar" : "en"
  const source = product?.metadata?.source_images || {}
  return source[locale]?.[0] || source[locale === "ar" ? "en" : "ar"]?.[0] || ""
}

export function findCmsProductForPath(payload, path, lang = "en") {
  const normalized = String(path || "").replace(/^\/+|\/+$/g, "")
  return (
    payload?.products?.find(
      product =>
        legacyProductPath(product, lang).replace(/\/+$/g, "") === normalized,
    ) || null
  )
}
